import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DISMISSED_KEY = 'fg_install_dismissed';

/**
 * Captures the browser's beforeinstallprompt event and shows a
 * tasteful banner after a short delay. Remembers dismissal in localStorage.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't do anything if already dismissed or already installed (standalone mode)
    if (
      localStorage.getItem(DISMISSED_KEY) ||
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone
    ) {
      return;
    }

    // On Android we promote the native Play Store app (AppInstallBanner), not the PWA.
    const isAndroid = /Android/i.test(navigator.userAgent || '');

    const handler = (e) => {
      // Always preventDefault so the browser's OWN automatic PWA install prompt is suppressed.
      // On Android we stop there (Play Store banner is the CTA); elsewhere we show our custom prompt.
      e.preventDefault();
      if (isAndroid) return;
      setDeferredPrompt(e);
      // Small delay so the prompt doesn't feel jarring on first load
      setTimeout(() => setVisible(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  if (!visible || !deferredPrompt) return null;

  return (
    <div
      role="dialog"
      aria-label="Install FindGambia app"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50
                 bg-card border border-border rounded-xl shadow-lg p-4
                 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Download className="h-5 w-5 text-primary" aria-hidden />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Install FindGambia</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Add to your home screen for quick access — works offline too.
        </p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={handleInstall} className="h-7 text-xs px-3">
            Install
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDismiss} className="h-7 text-xs px-3">
            Not now
          </Button>
        </div>
      </div>

      <button
        onClick={handleDismiss}
        className="text-muted-foreground hover:text-foreground flex-shrink-0 -mt-0.5"
        aria-label="Dismiss install prompt"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
